import React from "react";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import { Alert, Modal, Row, Col } from "components/bootstrap";
import { FormikFormGroup, ModalSubmit } from "components/common";
import { getCredentialsDefinition } from "data-tiering/repository-credentials";
import useRepositories from "./hooks/useRepositories";
import useRepositoryFormErrors from "./hooks/useRepositoryFormErrors";
import { isS3CompatibleRepositoryType, resolveRepositoryType, STORAGE_REPOSITORY_TYPE } from "./types";
const KNOWN_FIELDS = ["name", "bucket", "container", "base_path", "credentials"];
const CloudStorageRepositoryModalForm = ({ type, hideModal }) => {
  const { t } = useTranslation("enterprise_data_tiering");
  const { onCreateRepository } = useRepositories();
  const { formError, clearFormError, applyBackendErrors } = useRepositoryFormErrors(KNOWN_FIELDS);
  const definition = getCredentialsDefinition(type);
  const CredentialFields = definition == null ? void 0 : definition.create.Fields;
  const handleSubmit = (values, { setErrors }) => {
    const { bucket, container, credentials, ...baseValues } = values;
    const submitValues = {
      type: resolveRepositoryType(type),
      ...baseValues,
      ...credentials && {
        credentials: isS3CompatibleRepositoryType(type) || type === STORAGE_REPOSITORY_TYPE.S3 ? { ...credentials, provider: type } : credentials
      },
      ...bucket && { bucket },
      ...container && { container }
    };
    clearFormError();
    return onCreateRepository(submitValues).then(() => hideModal()).catch((error) => applyBackendErrors(error, setErrors));
  };
  const commonValues = {
    name: "",
    base_path: ""
  };
  const bucketLabel = type === "azure" ? "container" : "bucket";
  const bucketLabelText = t(`repository_form.bucket_label.${bucketLabel}`);
  const initialValues = {
    ...commonValues,
    [bucketLabel]: "",
    ...definition && { credentials: definition.create.initialValues(type) }
  };
  const validate = (values) => {
    var _a, _b, _c;
    const errors = {};
    if (!((_a = values.name) == null ? void 0 : _a.trim())) errors.name = t("repository_form.name_required");
    if (!((_b = values[bucketLabel]) == null ? void 0 : _b.trim()))
      errors[bucketLabel] = t("repository_form.bucket_required", { label: bucketLabelText });
    if (!((_c = values.base_path) == null ? void 0 : _c.trim())) errors.base_path = t("repository_form.base_path_required");
    if (definition) {
      const credentialErrors = definition.create.validate(values.credentials);
      if (Object.keys(credentialErrors).length > 0) errors.credentials = credentialErrors;
    }
    return errors;
  };
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      onSubmit: handleSubmit,
      initialValues,
      validate,
      validateOnMount: true
    },
    ({ isSubmitting, isValidating, isValid }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Body, null, formError && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, formError), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        type: "text",
        label: t("repository_form.name"),
        name: "name",
        help: t("repository_form.name_help"),
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        type: "text",
        label: bucketLabelText,
        name: bucketLabel,
        help: t(`repository_form.bucket_help.${bucketLabel}`),
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        type: "text",
        label: t("repository_form.base_path"),
        name: "base_path",
        help: t("repository_form.base_path_help"),
        required: true
      }
    ), CredentialFields && /* @__PURE__ */ React.createElement(CredentialFields, { type })))), /* @__PURE__ */ React.createElement(Modal.Footer, null, definition ? /* @__PURE__ */ React.createElement(
      ModalSubmit,
      {
        submitButtonText: t("repository_form.create"),
        submitLoadingText: t("repository_form.verifying_credentials"),
        onCancel: hideModal,
        disabledSubmit: isValidating || !isValid,
        isSubmitting,
        isAsyncSubmit: true
      }
    ) : /* @__PURE__ */ React.createElement(
      ModalSubmit,
      {
        submitButtonText: t("repository_form.create"),
        submitLoadingText: t("repository_form.creating"),
        onCancel: hideModal,
        disabledSubmit: isValidating || !isValid,
        isSubmitting
      }
    )))
  );
};
export default CloudStorageRepositoryModalForm;
